import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, CheckCircle } from 'lucide-react';

// Returns today's date as YYYY-MM-DD in LOCAL timezone (not UTC)
// This matches the backend's datetime.now() which also uses local time
const getLocalToday = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const styles = {
    backdrop: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 9000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px', // prevents card edge-to-edge on very narrow screens
    },
    card: {
        background: '#ffffff',
        borderRadius: '16px',
        padding: '24px',
        width: '100%',
        maxWidth: '400px',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
        position: 'relative',
        // Prevent backdrop click from propagating from the card itself
    },
    closeBtn: {
        position: 'absolute',
        top: '16px',
        right: '16px',
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        color: '#9ca3af',
        padding: '4px',
        borderRadius: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // 44×44 touch target
        width: '44px',
        height: '44px',
    },
    header: {
        marginBottom: '20px',
        paddingRight: '32px', // room for close button
    },
    title: {
        fontSize: '1.1rem',
        fontWeight: '700',
        color: '#111827',
        margin: '0 0 4px 0',
    },
    entityName: {
        fontSize: '0.875rem',
        color: '#6b7280',
        margin: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    fieldGroup: {
        marginBottom: '16px',
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '0.8125rem',
        fontWeight: '600',
        color: '#374151',
        marginBottom: '8px',
    },
    dateInput: {
        width: '100%',
        padding: '10px 12px',
        border: '1.5px solid #d1d5db',
        borderRadius: '10px',
        fontSize: '0.9375rem',
        color: '#111827',
        background: '#f9fafb',
        boxSizing: 'border-box',
        outline: 'none',
        cursor: 'pointer',
        // min height for 44px touch target
        minHeight: '44px',
    },
    checkboxRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '12px',
        background: '#eff6ff',
        borderRadius: '10px',
        border: '1.5px solid #bfdbfe',
        cursor: 'pointer',
        marginBottom: '16px',
        // 44px minimum height for touch
        minHeight: '44px',
    },
    checkbox: {
        width: '18px',
        height: '18px',
        accentColor: '#3b82f6',
        cursor: 'pointer',
        flexShrink: 0,
    },
    checkboxLabel: {
        fontSize: '0.875rem',
        fontWeight: '500',
        color: '#1d4ed8',
        cursor: 'pointer',
        userSelect: 'none',
    },
    footer: {
        display: 'flex',
        gap: '10px',
        marginTop: '4px',
    },
    cancelBtn: {
        flex: 1,
        padding: '12px',
        border: '1.5px solid #d1d5db',
        borderRadius: '10px',
        background: '#ffffff',
        color: '#374151',
        fontWeight: '600',
        fontSize: '0.875rem',
        cursor: 'pointer',
        minHeight: '44px',
    },
    confirmBtn: {
        flex: 2,
        padding: '12px',
        border: 'none',
        borderRadius: '10px',
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
        color: '#ffffff',
        fontWeight: '600',
        fontSize: '0.875rem',
        cursor: 'pointer',
        minHeight: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        opacity: 1,
        transition: 'opacity 0.15s ease',
    },
    confirmBtnDisabled: {
        opacity: 0.6,
        cursor: 'not-allowed',
    },
    spinner: {
        width: '16px',
        height: '16px',
        border: '2px solid rgba(255,255,255,0.4)',
        borderTopColor: '#ffffff',
        borderRadius: '50%',
        animation: 'spin 0.7s linear infinite',
        flexShrink: 0,
    },
};

// Inject keyframe once — only matters for the spinner animation
if (typeof document !== 'undefined' && !document.getElementById('add-visit-modal-styles')) {
    const style = document.createElement('style');
    style.id = 'add-visit-modal-styles';
    style.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
    document.head.appendChild(style);
}

export default function AddVisitModal({ isOpen, onClose, onConfirm, entityName, isLoading }) {
    const [selectedDate, setSelectedDate] = useState(getLocalToday());
    const [scheduledTime, setScheduledTime] = useState('');
    const [scheduledEndTime, setScheduledEndTime] = useState('');
    const [customerConfirmed, setCustomerConfirmed] = useState(false);

    // Reset state whenever the modal opens so it's always fresh
    useEffect(() => {
        if (isOpen) {
            setSelectedDate(getLocalToday());
            setScheduledTime('');
            setScheduledEndTime('');
            setCustomerConfirmed(false);
        }
    }, [isOpen]);

    // Close on Escape key
    useEffect(() => {
        if (!isOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // Customer confirmed checkbox only makes sense for future dates.
    // Scheduling for today means the visit is happening soon — confirmation is implicit.
    const isFutureDate = selectedDate > getLocalToday();

    // Generate all 30-minute slots for the day: ["06:00", "06:30", ... "22:30"]
    // Starting at 06:00 — no one books a sales visit at 1am
    const TIME_SLOTS = Array.from({ length: 34 }, (_, i) => {
        const totalMins = 360 + i * 30; // 360 = 6 * 60 (start at 06:00)
        const h = Math.floor(totalMins / 60);
        const m = totalMins % 60;
        const hh = String(h).padStart(2, '0');
        const mm = String(m).padStart(2, '0');
        const period = h < 12 ? 'AM' : 'PM';
        const h12 = h % 12 === 0 ? 12 : h % 12;
        return { value: `${hh}:${mm}`, label: `${h12}:${mm} ${period}` };
    });

    // Only show end-time slots that are strictly after the selected start
    const END_SLOTS = scheduledTime
        ? TIME_SLOTS.filter(s => s.value > scheduledTime)
        : TIME_SLOTS;

    // When start changes, clear end if it's no longer valid
    const handleStartTimeChange = (e) => {
        const newStart = e.target.value;
        setScheduledTime(newStart);
        if (scheduledEndTime && scheduledEndTime <= newStart) {
            setScheduledEndTime('');
        }
    };

    // When the date is changed back to today, clear the confirmed flag
    const handleDateChange = (e) => {
        const newDate = e.target.value;
        setSelectedDate(newDate);
        if (newDate <= getLocalToday()) {
            setCustomerConfirmed(false);
        }
    };

    const handleConfirm = () => {
        if (isLoading) return;
        onConfirm({
            scheduled_date: selectedDate,
            scheduled_time: isFutureDate && scheduledTime ? scheduledTime : null,
            scheduled_end_time: isFutureDate && scheduledTime && scheduledEndTime ? scheduledEndTime : null,
            // Always false for today — only meaningful for future dates
            customer_confirmed: isFutureDate ? customerConfirmed : false,
        });
    };

    if (!isOpen) return null;

    return (
        <div
            style={styles.backdrop}
            onClick={onClose} // clicking outside the card closes the modal
            role="dialog"
            aria-modal="true"
            aria-labelledby="add-visit-modal-title"
            data-testid="add-visit-modal"
        >
            {/* Card — stop click propagation so backdrop handler doesn't fire */}
            <div style={styles.card} onClick={(e) => e.stopPropagation()}>

                {/* Close button */}
                <button
                    style={styles.closeBtn}
                    onClick={onClose}
                    aria-label="Close"
                    disabled={isLoading}
                >
                    <X size={18} />
                </button>

                {/* Header */}
                <div style={styles.header}>
                    <p style={styles.title} id="add-visit-modal-title">Add to Route</p>
                    {entityName && (
                        <p style={styles.entityName} title={entityName}>{entityName}</p>
                    )}
                </div>

                {/* Date picker */}
                <div style={styles.fieldGroup}>
                    <label style={styles.label} htmlFor="add-visit-date">
                        <Calendar size={14} />
                        Visit date
                    </label>
                    <input
                        id="add-visit-date"
                        type="date"
                        value={selectedDate}
                        min={getLocalToday()} // prevent scheduling in the past
                        onChange={handleDateChange}
                        style={styles.dateInput}
                        disabled={isLoading}
                    />
                </div>

                {/* Start + End time — only shown for future dates */}
                {isFutureDate && (
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>
                            <Clock size={14} />
                            Visit time <span style={{color:'#9ca3af', fontWeight:400}}>(optional)</span>
                        </label>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <select
                                id="add-visit-time-start"
                                value={scheduledTime}
                                onChange={handleStartTimeChange}
                                style={{ ...styles.dateInput, flex: 1 }}
                                disabled={isLoading}
                            >
                                <option value="">Start</option>
                                {TIME_SLOTS.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                            <span style={{ color: '#9ca3af', fontSize: '0.8125rem', flexShrink: 0 }}>to</span>
                            <select
                                id="add-visit-time-end"
                                value={scheduledEndTime}
                                onChange={(e) => setScheduledEndTime(e.target.value)}
                                style={{ ...styles.dateInput, flex: 1, opacity: scheduledTime ? 1 : 0.4 }}
                                disabled={isLoading || !scheduledTime}
                            >
                                <option value="">End</option>
                                {END_SLOTS.map(s => (
                                    <option key={s.value} value={s.value}>{s.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                )}

                {/* Customer confirmed — only shown for future dates */}
                {isFutureDate && (
                    <div
                        style={styles.checkboxRow}
                        onClick={() => !isLoading && setCustomerConfirmed(prev => !prev)}
                    >
                        <input
                            id="add-visit-confirmed"
                            type="checkbox"
                            checked={customerConfirmed}
                            onChange={(e) => setCustomerConfirmed(e.target.checked)}
                            style={styles.checkbox}
                            disabled={isLoading}
                            // Prevent the div's onClick from firing twice on label click
                            onClick={(e) => e.stopPropagation()}
                        />
                        <label
                            htmlFor="add-visit-confirmed"
                            style={styles.checkboxLabel}
                            onClick={(e) => e.stopPropagation()} // already handled by div
                        >
                            Customer confirmed?
                        </label>
                    </div>
                )}

                {/* Footer buttons */}
                <div style={styles.footer}>
                    <button
                        style={styles.cancelBtn}
                        onClick={onClose}
                        disabled={isLoading}
                        data-testid="add-visit-modal-cancel"
                    >
                        Cancel
                    </button>
                    <button
                        style={{
                            ...styles.confirmBtn,
                            ...(isLoading ? styles.confirmBtnDisabled : {}),
                        }}
                        onClick={handleConfirm}
                        disabled={isLoading}
                        aria-busy={isLoading}
                        data-testid="add-visit-modal-confirm"
                    >
                        {isLoading ? (
                            <>
                                <span style={styles.spinner} aria-hidden="true" />
                                Adding...
                            </>
                        ) : (
                            <>
                                <CheckCircle size={15} />
                                Add to Route
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}
